import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import FoundingKeeper from "@/components/FoundingKeeper";

const tiers = [
  {
    name: "Hobbyist",
    monthly: 4.99,
    blurb: "For the backyard beekeeper finding their footing.",
    features: [
      "Up to 5 hives",
      "Basic inspection logs",
      "10 Ask a Bee™ credits/month",
      "Offline access",
      "Community support",
    ],
    comingSoon: false,
    featured: false,
    testid: "pricing-card-hobbyist",
  },
  {
    name: "Beekeeper",
    monthly: 9.99,
    blurb: "For growing apiaries that live in their records.",
    features: [
      "Up to 25 hives",
      "Full inspection logs and hive history",
      "BeeCredits™ rewards",
      "Harvest and expense reports",
      "50 Ask a Bee™ credits/month",
      "Email support",
    ],
    comingSoon: true,
    featured: false,
    testid: "pricing-card-beekeeper",
  },
  {
    name: "Apiary Pro",
    monthly: 19.99,
    blurb: "For sideliners and professionals who run on data.",
    features: [
      "Unlimited hives",
      "CPA-ready financial reports",
      "Advanced AI insights",
      "Priority support",
      "200 Ask a Bee™ credits/month",
      "All future features included",
    ],
    comingSoon: false,
    featured: true,
    testid: "pricing-card-apiary-pro",
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12" data-testid="pricing-hero">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-8">Pricing</p>
        </FadeUp>
        <h1 className="font-display font-medium tracking-tighter leading-[0.95] text-[clamp(2.6rem,6vw,5rem)] text-forest" data-testid="pricing-heading">
          <MaskedLines
            lines={[
              <span key="a">Simple as</span>,
              <span key="b"><em className="text-honey-dark not-italic font-semibold">honey.</em></span>,
            ]}
            delay={0.2}
          />
        </h1>
        <FadeUp delay={0.7} className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6">
          <p className="text-ink/60 leading-relaxed max-w-xl text-base" data-testid="pricing-note">
            Pick the plan that fits your apiary. Switch to annual and get 12 months
            for the price of 10.
          </p>
          <div className="flex items-center gap-1 bg-white border border-forest/15 rounded-full p-1 w-fit" data-testid="billing-toggle">
            <button
              type="button"
              data-testid="billing-monthly"
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${!annual ? "bg-forest text-bone" : "text-forest/60 hover:text-forest"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="billing-annual"
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${annual ? "bg-forest text-bone" : "text-forest/60 hover:text-forest"}`}
            >
              Annual <span className="text-honey-dark font-semibold">−2 months</span>
            </button>
          </div>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28" data-testid="pricing-tiers">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tiers.map((tier, i) => {
            const annualTotal = (tier.monthly * 10).toFixed(2);
            const crossedTotal = (tier.monthly * 12).toFixed(2);
            return (
              <FadeUp key={tier.name} delay={i * 0.1}>
                <div
                  data-testid={tier.testid}
                  className={`relative h-full rounded-[2rem] p-8 md:p-10 flex flex-col transition-colors duration-500 ${
                    tier.featured
                      ? "bg-forest text-bone border-2 border-honey"
                      : "bg-white border border-forest/10 hover:border-honey"
                  }`}
                >
                  {tier.comingSoon && (
                    <span
                      data-testid="pricing-beekeeper-badge"
                      className="absolute -top-4 left-8 bg-honey text-forest text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full"
                    >
                      Coming Soon
                    </span>
                  )}
                  <h2 className={`font-display text-3xl font-medium tracking-tight ${tier.featured ? "text-bone" : "text-forest"}`}>
                    {tier.name}
                  </h2>
                  <p className={`text-sm mt-2 mb-8 ${tier.featured ? "text-bone/60" : "text-ink/50"}`}>
                    {tier.blurb}
                  </p>
                  <div className="mb-8 min-h-24">
                    {annual ? (
                      <>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span
                            data-testid={`pricing-price-${tier.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                            className={`font-display text-5xl font-medium tracking-tighter ${tier.featured ? "text-honey" : "text-forest"}`}
                          >
                            ${annualTotal}
                          </span>
                          <span className={tier.featured ? "text-bone/50" : "text-ink/40"}>/ year</span>
                        </div>
                        <p className={`text-sm mt-1 ${tier.featured ? "text-bone/50" : "text-ink/40"}`}>
                          <span className="line-through">${crossedTotal}</span>{" "}
                          <span className="text-honey-dark font-semibold">2 months free</span>
                        </p>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-2">
                        <span
                          data-testid={`pricing-price-${tier.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                          className={`font-display text-5xl font-medium tracking-tighter ${tier.featured ? "text-honey" : "text-forest"}`}
                        >
                          ${tier.monthly}
                        </span>
                        <span className={tier.featured ? "text-bone/50" : "text-ink/40"}>/ month</span>
                      </div>
                    )}
                  </div>
                  <ul className="flex flex-col gap-3.5 mb-10">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-honey" : "text-honey-dark"}`} />
                        <span className={tier.featured ? "text-bone/80" : "text-ink/70"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {tier.comingSoon ? (
                    <span
                      data-testid={`pricing-cta-${tier.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className="mt-auto inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-full text-sm bg-forest/10 text-ink/40 cursor-not-allowed"
                    >
                      Coming Soon
                    </span>
                  ) : (
                    <Link
                      to="/pre-register?src=pricing"
                      data-testid={`pricing-cta-${tier.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className={`group mt-auto inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-full text-sm transition-colors duration-300 ${
                        tier.featured
                          ? "bg-honey text-forest hover:bg-honey-light"
                          : "bg-forest text-bone hover:bg-honey hover:text-forest"
                      }`}
                    >
                      Join the Launch List
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </FadeUp>
            );
          })}
        </div>
        <FadeUp delay={0.25} className="mt-12 text-center">
          <p className="text-sm text-ink/40" data-testid="pricing-disclaimer">
            Annual plans bill once per year at 10 months' cost — 2 months free.
            Founding Keepers lock in even lower rates below, forever.
          </p>
        </FadeUp>
      </section>

      <FoundingKeeper />
    </>
  );
}
